import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@material-ui/core";
import { Remove } from "@material-ui/icons";
import Add from "@material-ui/icons/Add";
import React, { useCallback, useEffect, useState } from "react";
import {
  createMarketingCampaign,
  editMarketingCampaign,
  getMarketingCampaignById,
} from "../../api/marketingService";
import { getAllStates } from "../../api/statesService";
import { getAllCities } from "../../api/cityService";
import { showSuccess } from "../../redux/actions/snackbarActions";
import { Store } from "../../redux/store";
import ImageSelect from "../../shared/components/ImageSelect";
import { downloadFile } from "../../shared/helpers/downloadFile";

const MarketingCampaignForm = ({ id = undefined, open, onClose }) => {
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [description, setDescription] = useState("");
  const [banners, setBanners] = useState([]);
  const [bannersAmount, setBannerAmount] = useState(1);
  const [position, setPosition] = useState(1);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState("");
  const [selectedCityData, setSelectedCityData] = useState({});

  const isSaveButtonDisabled = !(
    name &&
    companyName &&
    description &&
    selectedState &&
    selectedCityId &&
    position &&
    banners.every((banner) => banner && banner.image)
  );

  const fetchStates = useCallback(async () => {
    try {
      const response = await getAllStates();
      setStates(response);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const fetchCities = useCallback(async (stateCode) => {
    try {
      const response = await getAllCities(stateCode);
      setCities(response);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const addBanner = useCallback(() => {
    setBannerAmount((amount) => (amount < 3 ? amount + 1 : amount));
  }, []);

  const removeBanner = useCallback(() => {
    setBannerAmount((amount) => (amount > 1 ? amount - 1 : amount));
  }, []);

  const setBannerImage = useCallback(
    (evt, index) => {
      if (evt && evt.currentTarget && evt.currentTarget.files[0]) {
        const newBanners = [...banners];
        newBanners[index] = { image: evt.currentTarget.files[0] };
        setBanners(newBanners);
      } else {
        const newBanners = [...banners.filter((_, bi) => bi !== index)];
        setBanners(newBanners);
      }
    },
    [banners]
  );

  const cropBanner = useCallback(
    async (img, index) => {
      if (img) {
        const banner = banners[index];
        banner.crop = img;
        banners[index] = banner;
        setBanners([...banners]);
      } else {
        setBanners([]);
      }
    },
    [banners]
  );

  const cleanForm = useCallback(() => {
    setName(undefined);
    setDescription(undefined);
    setCompanyName(undefined);
    setBanners([]);
    setBannerAmount(1);
    setPosition(1);
  }, []);

  const handleStateChange = (event) => {
    const stateCode = event.target.value;
    setSelectedState(stateCode);
    setSelectedCity(""); // Redefinir a cidade selecionada ao alterar o estado
    fetchCities(stateCode);
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setSelectedCityId(cityId);

    const selectedCityData = cities.find((city) => city.id === cityId);
    setSelectedCityData(selectedCityData);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (id) {
        await editMarketingCampaign(
          id,
          name,
          companyName,
          description,
          banners.map((x) => x.crop),
          position,
          selectedCityId
        );
        Store.dispatch(showSuccess("Marketing Campaign updated successfully!"));
      } else {
        await createMarketingCampaign(
          name,
          companyName,
          description,
          banners.map((x) => x.crop),
          position,
          selectedCityId
        );
        Store.dispatch(showSuccess("Marketing Campaign created successfully!"));
      }
      onClose();
      cleanForm();
    } catch (err) {
      // Tratar o erro, se necessário
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    async function buscarPorId() {
      if (id) {
        setFetching(true);
        const response = await getMarketingCampaignById(id);
        setName(response.name);
        setCompanyName(response.companyName);
        setDescription(response.description);
        setBannerAmount(response.banners.length);
        setPosition(response.position);

        const fileBanners = [];
        for (let banner of response.banners) {
          const file = await downloadFile(banner);
          fileBanners.push({ image: file, crop: file });
        }
        setBanners(fileBanners);
        setFetching(false);

        if (response.idCity) {
          setSelectedCityId(response.idCity);
          if (response.state) {
            const stateCode = response.state;
            setSelectedState(stateCode);
            fetchCities(stateCode);
          }
        } else {
          setSelectedCityId("");
          setSelectedState("");
          setCities([]);
        }
      }
    }
    buscarPorId();
  }, [id]);

  useEffect(() => {
    if (!selectedCityId) {
      setSelectedState("");
      setCities([]);
    }
  }, [selectedCityId]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        cleanForm();
      }}
    >
      <DialogTitle onClose={onClose}>
        {!!id ? "Editing" : "Creating"} Marketing Campaign
      </DialogTitle>
      <DialogContent>
        {fetching && <CircularProgress />}
        <TextField
          margin="normal"
          id="name"
          variant="outlined"
          label="Name"
          type="text"
          InputLabelProps={{ shrink: true }}
          onChange={({ target }) => setName(target.value)}
          value={name}
          disabled={fetching}
          fullWidth
        />

        <TextField
          margin="normal"
          id="companyName"
          variant="outlined"
          label="Company Name"
          type="text"
          InputLabelProps={{ shrink: true }}
          onChange={({ target }) => setCompanyName(target.value)}
          value={companyName}
          disabled={fetching}
          fullWidth
        />
        <TextField
          margin="normal"
          id="description"
          variant="outlined"
          label="Description"
          InputLabelProps={{ shrink: true }}
          type="text"
          onChange={({ target }) => setDescription(target.value)}
          value={description}
          disabled={fetching}
          fullWidth
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel htmlFor="state-select">State</InputLabel>
              <Select
                value={selectedState}
                onChange={handleStateChange}
                inputProps={{
                  name: "state",
                  id: "state-select",
                }}
              >
                {states.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            {selectedState && selectedCityData && (
              <FormControl fullWidth>
                <InputLabel htmlFor="city-select">City</InputLabel>
                <Select
                  value={selectedCityId}
                  onChange={handleCityChange}
                  inputProps={{
                    name: "city",
                    id: "city-select",
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>
        <FormControl component="fieldset" margin="normal">
          <FormLabel component="legend">Position</FormLabel>
          <RadioGroup
            aria-label="position"
            name="position"
            value={position}
            onChange={(e) => setPosition(parseInt(e.target.value))}
            row
          >
            <FormControlLabel
              value={2}
              control={<Radio />}
              label="Horizontal"
            />
            <FormControlLabel value={1} control={<Radio />} label="Square" />
          </RadioGroup>
        </FormControl>

        <Box style={{ display: "flex", alignItems: "center" }}>
          {bannersAmount > 1 && (
            <IconButton onClick={removeBanner}>
              <Remove />
            </IconButton>
          )}
          {bannersAmount < 3 && (
            <IconButton onClick={addBanner}>
              <Add />
            </IconButton>
          )}
        </Box>
        {Array.from(new Array(bannersAmount)).map((_, index) => (
          <ImageSelect
            key={index}
            showCrop
            style={{ width: "100%" }}
            image={
              banners[index]
                ? URL.createObjectURL(banners[index]?.image)
                : undefined
            }
            selectText="Choose an image"
            onChangeImage={(evt) => setBannerImage(evt, index)}
            onCleanImage={() => {
              setBannerImage(null, index);
              cropBanner(null, index);
            }}
            onCompleteCrop={(img) => cropBanner(img, index)}
            position={position}
          />
        ))}
      </DialogContent>
      <DialogActions>
                <Button color="primary" onClick={() => {
                    cleanForm();
                    onClose();
                }}>Back</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={saving || isSaveButtonDisabled}
                    onClick={handleSubmit}
                >
                    {saving ? <CircularProgress /> : (!!id ? 'Update' : 'Save')}
                </Button>
            </DialogActions>
    </Dialog>
  );
};

export default MarketingCampaignForm;